<script lang="ts">
  import { AdClipType, PlayerEvent, type PlayerInterface } from '@foundation-player/loader';
  import { onMount, onDestroy, setContext, getContext } from 'svelte';
  import Controls from './Controls.svelte';
  import MediumInfo from './MediumInfo.svelte';
  import PrimeControls from './PrimeControls.svelte';
  import type { PlayerStateContext } from '../../shared/types/player-state';
  import type { ContentLayerContext } from '../types/internal/content-layer';
  import type { MediaModeOptions } from '../types/internal/media-mode';
  import type { UIConfig } from '../types/ui-config';
  import type { UIStateContext } from '../types/internal/ui-state';
  import type { MenuContext } from '../../shared/types/menu';

  /** The element which should be set to fullscreen */
  export let fullscreenElement: HTMLElement;

  /**
   * The instance of the Foundation player.
   */
  export let player: PlayerInterface | undefined;

  /**
   * Called when the user activates the start button.
   *
   * When {@link UIConfig.createPlayerOnDemand} is set to true, the player
   * should get created now.
   * If the player already exists, the play method on the player is called. In
   * this case, this method can be used as a hook for tracking or other things.
   */
  export let onStart: undefined | (() => void);

  const {
    mode,
    hasMediaInfo,
    hasPrimeControls,
    shouldHideContentLayerOnClick,
    shouldHideContentLayerOnIdle,
    shouldHideControlsOnSessionEnd,
  } = getContext<MediaModeOptions>('mediaModeOptions');

  const { experimental } = getContext<UIConfig>('uiConfig');

  let layerElement: HTMLElement;
  let isIdle = false;
  let idleTimeout: ReturnType<typeof setTimeout>;
  let layerHeight = 300;
  let resizeObserver: ResizeObserver;

  /** Clicking the layer should force hide it */
  let isLayerHiddenByClick = false;

  /** We should NOT hide the layer when a menu is open */
  let isMenuOpen = false;

  const { adClip, linearAdPlaying, playing, stalling, sessionState } = getContext<PlayerStateContext>('playerState');
  const { waitingForFirstInteraction } = getContext<UIStateContext>('uiState');

  $: loading = $sessionState === 'loading';

  /**
   * The focusout event on the menu and the click event on the layer (top
   * container) are asynchronous. That means that the menu is already closed
   * when the click (that triggered the focusout) is handled.
   * Therefore, we need a grace period after closing the menu, in which the
   * click is ignored.
   * Tests revealed that a period of 200ms is fine, while 100ms is not enough.
   * This value might have to be refined depending on the experience on various
   * devices.
   * What could go wrong? If the 200ms are too short, the layer gets hidden
   * (but re-appears on the next mousemove/keystroke).
   */
  let menuClosedTimestamp = 0;
  let menuClosedGracePeriod = 200;

  /** Custom svelte events do not bubble, so we use a context for
   * direct communication with deep children */
  setContext<MenuContext>('menu', {
    setMenuOpen(isOpen: boolean) {
      isMenuOpen = isOpen;
      menuClosedTimestamp = isOpen ? 0 : Date.now();

      if (isOpen) {
        // keep the layer visible
        clearTimeout(idleTimeout);
      } else {
        // start timer to hide layer
        handleInteraction();
      }
    },
  });

  setContext<ContentLayerContext>('contentLayer', {
    startPlayer,
  });

  onMount(() => {
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver((entries) => {
        if (!Array.isArray(entries) || entries.length === 0) {
          return;
        }
        const height = entries[0].contentRect.height;
        updateSize(height);
      });

      resizeObserver.observe(layerElement);

      return () => resizeObserver.unobserve(layerElement);
    } else {
      // For browsers without support for ResizeObserver (most likely old TVs or
      // set-top boxes), we read the size of the layer once. We assume/hope
      // that it does not change afterward.
      const height = layerElement.getBoundingClientRect().height;
      updateSize(height);
    }
  });

  onDestroy(() => {
    if (player) {
      player.removeEventListener(PlayerEvent.MediaStarted, onMediaStarted);
    }
  });

  $: if (player) {
    initPlayerEventsAndState();
  }

  function updateSize(height: number) {
    layerHeight = Math.trunc(height);
  }

  function startPlayer() {
    waitingForFirstInteraction.set(false);

    if (onStart) {
      onStart();
    }

    if (player) {
      player.play();
    }
  }

  const onMediaStarted = () => {
    // if player started with autoplay we do not need additional user interaction
    waitingForFirstInteraction.set(false);
    markNotIdleAndTriggerIdleTimeout();
  };

  function initPlayerEventsAndState() {
    if (!player) {
      return;
    }

    player.addEventListener(PlayerEvent.MediaStarted, onMediaStarted);
  }

  /**
   * The layer covers the entire video. By simply clicking on it (and no other
   * control element) we can control the play/pause state as if we were clicking
   * on the PlayPause button.
   *
   * In case that an ad is playing, we instead call the openClickThrough method
   * of the advertising adapter.
   */
  function handleLayerClick(event: MouseEvent) {
    if (loading || isMenuOpen || Date.now() - menuClosedTimestamp < menuClosedGracePeriod) {
      // reset timestamp to not ignore a second click within the grace period
      menuClosedTimestamp = 0;
      return;
    }

    isLayerHiddenByClick = false;

    if ($waitingForFirstInteraction) {
      startPlayer();
    } else if ($linearAdPlaying && $adClip?.hasClickThrough) {
      // TODO: With the arrival of the new AdvertisementLayer, the layer is no
      //  longer visible during linear ads. So we should eventually remove the
      //  special ad handling in this component and its children.

      // Note: This is identical to the click event on the video element that plays the ad itself.
      player?.getAdvertisingAdapter()?.openClickThrough();
    } else {
      isLayerHiddenByClick = true;
      // prevent event from bubbling up to the layer, where it would trigger
      // an interaction, which would set isIdle to false
      event.stopPropagation();
    }
  }

  /**
   * Once the mouse and keyboard are idling, we can hide the layer and its
   * controls.
   */
  function handleInteraction() {
    if ($waitingForFirstInteraction || loading || isMenuOpen) {
      return;
    }

    isLayerHiddenByClick = false;
    markNotIdleAndTriggerIdleTimeout();
  }

  /**
   * hides the click-catcher and makes the layer visible
   * while also triggering the timeout to hide it again
   * @param event
   */
  function hideClickCatcher(event: MouseEvent | TouchEvent) {
    if (isIdle || isLayerHiddenByClick) {
      // prevent the browser from processing emulated mouse events
      // as otherwise these elements trigger the layer
      event.preventDefault();

      isLayerHiddenByClick = false;
      markNotIdleAndTriggerIdleTimeout();
    }
  }

  $: isLayerHidden =
    shouldHideContentLayerOnIdle &&
    !isMenuOpen &&
    $sessionState === 'active' &&
    (isLayerHiddenByClick || (isIdle && $playing));
  $: shouldHideBottomControls =
    (shouldHideControlsOnSessionEnd && $sessionState === 'ended') ||
    (mode === 'video' && experimental?.videoStartLayer === 'minimal' && $waitingForFirstInteraction);
  $: shouldHandleLayerClick = shouldHideContentLayerOnClick || $waitingForFirstInteraction;

  /**
   * Sets the idle flag with a 3s delay.
   */
  const markNotIdleAndTriggerIdleTimeout = () => {
    isIdle = false;

    clearTimeout(idleTimeout);
    idleTimeout = setTimeout(() => {
      isIdle = true;
    }, 3000);
  };
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  class="content-layer"
  class:idle={isLayerHidden}
  class:has-linear-ad={$linearAdPlaying}
  class:has-interactive-ad={$adClip?.isInteractive}
  class:has-overlay-ad={$adClip?.type === AdClipType.Overlay}
  data-testid="content-layer"
  bind:this={layerElement}
  on:click={shouldHideContentLayerOnIdle ? handleInteraction : null}
  on:mousemove={shouldHideContentLayerOnIdle ? handleInteraction : null}
  on:keydown={shouldHideContentLayerOnIdle ? handleInteraction : null}
  on:touchmove={shouldHideContentLayerOnIdle ? handleInteraction : null}
  on:focusin={shouldHideContentLayerOnIdle ? handleInteraction : null}
  style="--content-layer-height: {layerHeight}px;"
>
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div
    class="top"
    class:interactive={shouldHandleLayerClick}
    on:click|self={shouldHandleLayerClick ? handleLayerClick : null}
    data-testid="content-layer_top"
  >
    {#if hasPrimeControls && !$stalling && !$linearAdPlaying}
      <div class="prime-controls" class:is-loading={loading}>
        <PrimeControls {player} focusLayer={'content'} />
      </div>
    {/if}
  </div>

  {#if !shouldHideBottomControls}
    <div class="bottom" class:is-loading={loading}>
      {#if hasMediaInfo}
        <MediumInfo />
      {/if}
      {#if !$linearAdPlaying}
        <Controls {player} {fullscreenElement} focusLayer={'content'} />
      {/if}
    </div>
  {/if}
</div>

<!--
 Goal: When the layer is not visible, click/touch-events should not trigger
 actions of child elements but only make the layer visible.
 We have to listen to one touch-event as otherwise the browser-emulation
 results in two events and we couldn't prevent that
-->
{#if shouldHideContentLayerOnIdle}
  <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
  <div
    class="click-catcher"
    class:active={isLayerHidden}
    data-testid="click-catcher"
    on:click={hideClickCatcher}
    on:mousemove={hideClickCatcher}
    on:touchend={hideClickCatcher}
  ></div>
{/if}

<style module>
  .content-layer :global(*) {
    box-sizing: border-box;

    /**
     * Many browsers add a (blue) background to interactive elements while the
     * user taps them. This improves the user experience, because now the user
     * gets feedback on which elements are interactive. Compare this to the
     * mouse cursor shown as a hand or arrow when over buttons, links etc.
     * In the context of the player ui, this is not necessary, because it should
     * be very clear which elements are interactive. Especially when tapping the
     * top part of the layer, the (blue) background might be annoying, because
     * it covers a large part of the video. Therefore, we remove it.
     *
     * Note: This is a non-standard property and might cease to work at any
     * time.
     */
    -webkit-tap-highlight-color: transparent;
  }

  .content-layer {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    flex-direction: column;

    opacity: 0;
    pointer-events: auto;
    transition: opacity 0.5s ease-out;

    /* TODO: use background: rgba(0, 0, 0, 0.5) for small screens */
    background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.5));
  }

  :global([data-theme='light']) .content-layer {
    background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.5));
  }

  :global([data-mode='audio']) .content-layer {
    position: relative;
    background-image: none;
  }

  .top {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .top.interactive {
    cursor: pointer;
  }

  :global([data-mode='video']) .top:not(:only-child) {
    /*
     * When the controls are shown (and thus the top element is not the only
     * child), we add some padding to visually place the prime controls more in
     * the center of the layer
     */
    padding-top: 3%;
  }

  .bottom {
    display: flex;
    flex-direction: column;
    gap: 0.5em;
    padding: 0 20px 16px; /* Fallback for older browsers */
    /* Min value (8px) ends at 429 px. Max value (40px) starts at 1000 px. */
    /* https://modern-fluid-typography.vercel.app/ */
    padding: 0 clamp(1.25em, 7.5cqw - 0.75em, 3.75em) clamp(8px, 5.6cqw - 1em, 40px);
  }

  :global([data-mode='audio']) .bottom {
    gap: clamp(0em, 3cqw - 0.75em, 0.75em);
    padding: 18px 18px 8px;
  }

  .content-layer:not(.idle) {
    opacity: 1;
    transition: opacity 50ms ease-in;
  }

  /* TODO: Is the controls layer responsible for hiding during ads or is the
      Player component? */
  .content-layer.has-linear-ad,
  .content-layer.has-interactive-ad,
  .content-layer.has-overlay-ad {
    visibility: hidden;
  }

  .click-catcher {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: none;
  }

  .click-catcher.active {
    display: block;
    cursor: none;
  }

  .is-loading {
    visibility: hidden;
  }

  @media (prefers-contrast: more) {
    :global([data-mode='video']) .bottom {
      background: var(--controls-contrast-background);
      /* We use a box-shadow shifted to the top to also cover the time display */
      box-shadow: 0 -2em 0 var(--controls-contrast-background);
    }
  }

  @media (prefers-contrast: more) and (prefers-reduced-transparency: reduce) {
    :global([data-mode='video']) .bottom {
      background: var(--controls-no-transparency-background);
      box-shadow: 0 -1.5em 0 var(--controls-no-transparency-background);
    }
  }
</style>
